import Button from 'devextreme-react/button';
import TextBox from 'devextreme-react/text-box';
import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { createBlankStarterInUserStorage } from '../fileBrowser/createBlankStarterInUserStorage';
import { USER_STORAGE_MOUNT_ID } from '../storageFileAudit/formatters';
import { showErrorToast } from '../notifications/toast';
import '../codeEditor/codeEditorCreate.css';
import {
  TEXT_EDITOR_TEMPLATES,
  defaultTextEditorFileName,
  validateTextEditorFileName,
  type TextEditorTemplate,
  type TextEditorTemplateId
} from './textEditorTemplates';

export type TextEditorCreateResult = {
  mountId: string;
  relativePath: string;
  fileName: string;
  starterId: TextEditorTemplateId;
};

export type TextEditorCreatePanelProps = {
  intro?: ReactNode;
  secondaryAction?: ReactNode;
  mountId?: string;
  onCreated: (result: TextEditorCreateResult) => void;
};

export function TextEditorCreatePanel({
  intro = 'Pick a plain-text template, name the file, then open it in the Text Editor.',
  secondaryAction,
  mountId = USER_STORAGE_MOUNT_ID,
  onCreated
}: TextEditorCreatePanelProps) {
  const [selectedId, setSelectedId] = useState<TextEditorTemplateId>('txt');
  const [fileName, setFileName] = useState(() => defaultTextEditorFileName('txt'));
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const selected = useMemo(
    () => TEXT_EDITOR_TEMPLATES.find((t) => t.starterId === selectedId) ?? TEXT_EDITOR_TEMPLATES[0],
    [selectedId]
  );

  const selectTemplate = useCallback((template: TextEditorTemplate) => {
    setSelectedId(template.starterId);
    setFileName(defaultTextEditorFileName(template.starterId));
    setError(null);
  }, []);

  const createAndOpen = useCallback(async () => {
    const validationError = validateTextEditorFileName(fileName, selected.extension);
    if (validationError) {
      setError(validationError);
      return;
    }

    setBusy(true);
    setError(null);
    try {
      const created = await createBlankStarterInUserStorage(
        selected.starterId,
        mountId,
        fileName.trim()
      );
      onCreated({
        mountId: created.mountId,
        relativePath: created.relativePath,
        fileName: created.fileName,
        starterId: selected.starterId
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Could not create text file.';
      setError(message);
      showErrorToast(message);
      setBusy(false);
    }
  }, [fileName, mountId, onCreated, selected.extension, selected.starterId]);

  return (
    <div className="code-editor-create">
      {intro ? <p className="code-editor-create__intro">{intro}</p> : null}

      <section className="code-editor-create__templates" aria-label="Text file templates">
        {TEXT_EDITOR_TEMPLATES.map((template) => {
          const active = template.starterId === selected.starterId;
          return (
            <button
              key={template.starterId}
              type="button"
              className={
                active
                  ? 'code-editor-create__card code-editor-create__card--active'
                  : 'code-editor-create__card'
              }
              aria-pressed={active}
              disabled={busy}
              onClick={() => selectTemplate(template)}
            >
              <span className="code-editor-create__card-title">{template.title}</span>
              <span className="code-editor-create__card-ext">{template.extension}</span>
              <span className="code-editor-create__card-desc">{template.description}</span>
            </button>
          );
        })}
      </section>

      <section className="code-editor-create__form" aria-label="Create file">
        <label className="code-editor-create__label" htmlFor="text-editor-create-filename">
          File name
        </label>
        <TextBox
          value={fileName}
          disabled={busy}
          stylingMode="outlined"
          inputAttr={{ id: 'text-editor-create-filename' }}
          onValueChanged={(e) => setFileName(String(e.value ?? ''))}
          onEnterKey={() => void createAndOpen()}
        />
        <p className="code-editor-create__hint">
          {selected.extension === '.env'
            ? 'Use .env or a name ending in .env.'
            : `Must use the ${selected.extension} extension for ${selected.title}.`}
        </p>
        {error ? (
          <p className="code-editor-create__error" role="alert">
            {error}
          </p>
        ) : null}
        <div className="code-editor-create__actions">
          <Button
            text={busy ? 'Creating…' : 'Create and open'}
            type="default"
            disabled={busy}
            onClick={() => void createAndOpen()}
          />
          {secondaryAction}
        </div>
      </section>
    </div>
  );
}
